import { Injectable, inject, signal } from '@angular/core';
import { useMutation } from '@globalconnect/query';
import saveAs from 'file-saver';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  map,
  Observable,
  Subject,
  take,
} from 'rxjs';
import { FeedbackAlertService } from '../../../../shared/feedback-alert/service/feedback-alert.service';
import { ImageCompressService } from '../../../../shared/image-compress/image-compress.service';
import { ImagePreviewV2File } from '../../../../shared/image-preview-v2/image-preview-v2.entities';
import { createUrlForProtectedImg } from '../../../../shared/util/image-helper';
import { InstallationReportingMduApiService } from '../../installation-reporting-mdu-api.service';
import {
  FileMetaV2Response,
  ListWrapperEntity,
} from '../model/installation-reporting-mdu-bff.entities';

@Injectable({ providedIn: 'root' })
export class InstallationReportingMduSelfInspectionService {
  private readonly api = inject(InstallationReportingMduApiService);
  private readonly imageCompressService = inject(ImageCompressService);
  private readonly feedbackAlertService = inject(FeedbackAlertService);

  private readonly _isMutationLoadingState = signal<Record<string, boolean>>(
    {},
  );
  readonly isMutationLoadingState = this._isMutationLoadingState.asReadonly();

  readonly answerChanged = new Subject<string>();
  readonly uploadSucceeded = new Subject<{
    definitionId: string;
    attachments: FileMetaV2Response[];
  }>();

  private readonly _previewImages = signal<ImagePreviewV2File[]>([]);
  readonly previewImages = this._previewImages.asReadonly();
  private readonly _previewLoading = new BehaviorSubject(false);
  readonly previewLoading = this._previewLoading.asObservable();

  setPreviewImages(files: { fileId: string; fileName: string }[]) {
    this._previewImages.set([]);
    this.loadPreviewImages(files);
  }

  removePreviewImage(fileId: string) {
    this._previewImages.update((images) =>
      images.filter((image) => image.fileId !== fileId),
    );
  }

  clearPreviewImages() {
    this._previewImages.set([]);
  }

  private addPreviewImages(files: { fileId: string; fileName: string }[]) {
    const shown = this._previewImages();
    this.loadPreviewImages(
      files.filter(
        ({ fileId }) => !shown.some((image) => image.fileId === fileId),
      ),
    );
  }

  private loadPreviewImages(files: { fileId: string; fileName: string }[]) {
    if (!files.length) {
      return;
    }
    this._previewLoading.next(true);
    forkJoin(files.map((file) => this.loadPreviewImage(file)))
      .pipe(
        catchError(() => EMPTY),
        finalize(() => this._previewLoading.next(false)),
      )
      .subscribe((images) =>
        this._previewImages.update((current) => [...current, ...images]),
      );
  }

  private loadPreviewImage({
    fileId,
    fileName,
  }: {
    fileId: string;
    fileName: string;
  }): Observable<ImagePreviewV2File> {
    return this.api.getFile(fileId).pipe(
      map((response) => ({
        fileId,
        fileName,
        imageUrl: createUrlForProtectedImg(response.body as BlobPart, fileName),
      })),
    );
  }

  transformAndUploadSelfInspectionImages(
    files: File[],
    definitionId: string,
    installationReportId: string,
  ) {
    if (!files.length) {
      return;
    }
    return Promise.all(
      files.map((file) => this.imageCompressService.compressFile(file)),
    ).then((compressedFiles) =>
      this.uploadSelfInspectionImages(
        installationReportId,
        definitionId,
        compressedFiles,
      ),
    );
  }

  uploadSelfInspectionImages = (
    installationReportId: string,
    definitionId: string,
    files: File[],
  ) =>
    useMutation(
      () =>
        this.api.uploadSelfInspectionFiles(
          installationReportId,
          definitionId,
          files,
        ),
      {
        onMutate: () => this.onMutationStarted(definitionId),
        onSuccess: (response: ListWrapperEntity<FileMetaV2Response>) =>
          this.onUploadSucceeded(definitionId, response),
        onError: () => this.onMutationFailed(definitionId, 'upload'),
      },
    ).mutate();

  deleteSelfInspectionImage = (
    installationReportId: string,
    definition: { id: string },
    fileId: string,
  ) =>
    useMutation(
      () =>
        this.api.deleteSelfInspectionFile(
          installationReportId,
          definition.id,
          fileId,
        ),
      {
        onMutate: () => this.onMutationStarted(definition.id),
        onSuccess: () => this.onDeleteSucceeded(definition.id, fileId),
        onError: () => this.onMutationFailed(definition.id, 'remove'),
      },
    ).mutate();

  private onMutationStarted(definitionId: string) {
    this.setMutationLoadingStateByDefinitionId(true, definitionId);
  }

  private onMutationFailed(definitionId: string, action: 'upload' | 'remove') {
    this.setMutationLoadingStateByDefinitionId(false, definitionId);
    this.feedbackAlertService.showAlert(
      'critical',
      `feedbackAlert.${action}.error`,
    );
  }

  private onUploadSucceeded(
    definitionId: string,
    response: ListWrapperEntity<FileMetaV2Response>,
  ) {
    const attachments = response.items ?? [];
    this.uploadSucceeded.next({ definitionId, attachments });
    this.addPreviewImages(attachments);
    this.answerChanged.next(definitionId);
    this.feedbackAlertService.showAlert(
      'success',
      'feedbackAlert.upload.success',
    );
  }

  private onDeleteSucceeded(definitionId: string, fileId: string) {
    this.removePreviewImage(fileId);
    this.answerChanged.next(definitionId);
    this.feedbackAlertService.showAlert(
      'success',
      'feedbackAlert.remove.success',
    );
  }

  downloadImages(files: { fileId: string; fileName: string }[]) {
    files.forEach(({ fileId, fileName }) => {
      this.api
        .getFile(fileId)
        .pipe(take(1))
        .subscribe((response) => {
          response.body && fileName && saveAs(response.body, fileName);
        });
    });
  }

  setMutationLoadingStateByDefinitionId = (
    loadingState: boolean,
    definitionId: string,
  ): void => {
    this._isMutationLoadingState.update((prev) => ({
      ...prev,
      [definitionId]: loadingState,
    }));
  };
}
